import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {reporter} from 'common/report/error-reporting';
import {
  FunnelOrder,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  TopLevelMetricType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {SPECIAL_EVENTS, XAU_ONLY_MATH_TYPES} from 'common/report/constants';
import {FUNNELS_TIME_TO_CONVERSION, PAGE_DURATION} from 'common/query-builder/query-block/properties-menu/constants';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {FlowsBookmark} from 'common/report/flows/models/types';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaShowClause} from 'common/util/formulas';
import {Behavior} from 'common/types/reports/query';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {EntryWithDisplay} from 'common/query-builder/query-entry-section/types';
import {INSIGHTS_COHORT_TYPE} from 'common/widgets/property-filter-menu/models/insights-filter';

import {
  BehaviorMetricDisplay,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaShowClause as FormulaShowClauseType,
  FormulaShowClause,
  GroupClause as GroupClauseType,
  InsightsChartParams,
  isInsightsBookmarkParams,
  MetricBehavior,
  MetricMetadata,
  ShowClause as ShowClauseType,
  ShowClauseFormula,
} from '../util/types';

export function displayOptionsMatchAxis(metricDisplay: BehaviorMetricDisplay, axis: BookmarkTypes.AxisAssignment) {
  // Since primary is the default, if axis is undefined we treat it as Primary;
  if (axis === BookmarkTypes.AxisAssignment.Primary) {
    return metricDisplay?.axis !== BookmarkTypes.AxisAssignment.Secondary;
  }
  return metricDisplay?.axis === BookmarkTypes.AxisAssignment.Secondary;
}

/**
 * This checks whether the display options for a metric have been set via the
 * metric units menu.
 *
 * NOTE: This ignores axis assignment. I.e. if a user assigns a metric to an axis
 * this will still be considered default.
 */
export function displayOptionsAreDefault(metricDisplay: BehaviorMetricDisplay) {
  const keys: Array<keyof BehaviorMetricDisplay> = [`abbrev`, `precision`, `prefix`, `suffix`];
  return isEmpty(metricDisplay) || keys.every((key) => isNil(metricDisplay[key]));
}

export function sanitizeIfCustomProperty(property: BookmarkTypes.BehaviorMeasurement[`property`]) {
  if (!property.customPropertyId) {
    return property;
  }

  const {customProperty, ...rest} = property;
  return {
    ...rest,
    // customProperty (custom property definition) should not be passed as an attr for persisted
    // custom properties. However, old bookmark did store customProperty for persisted custom
    // properties, which we remove below.
    ...(!isPersistedCustomProperty(property.customPropertyId) && customProperty ? {customProperty} : {}),
  };
}

export function isBehaviorShowClause(showClause: ShowClauseType | null): showClause is BehaviorShowClauseType {
  if (!showClause) {
    return false;
  }
  if (`id` in showClause && showClause?.type === TopLevelMetricType.Metric) {
    return true;
  }

  if (`behavior` in showClause && `measurement` in showClause) {
    return true;
  }

  if (`behavior` in showClause) {
    reporter.error(`Non-behavior show clause has unexpected "behavior" key`, {showClause});
  }
  return false;
}

export function isFormulaShowClause(showClause: ShowClauseType | null): showClause is FormulaShowClauseType {
  if (!showClause) {
    return false;
  }
  const oldCheck = `formula` in showClause;
  const newCheck = showClause.type === TopLevelMetricType.Formula;
  return newCheck || oldCheck;
}

function isSavedFormulaShowClause(showClause: ShowClauseType | null): boolean {
  return isFormulaShowClause(showClause) && !!showClause.id;
}

export function isSavedMetricShowClause(showClause: ShowClauseType): boolean {
  return showClause.type === TopLevelMetricType.Metric && Boolean(showClause.id);
}

export function isSavedMetricOrFormula(showClause: ShowClauseType): boolean {
  return isSavedMetricShowClause(showClause) || isSavedFormulaShowClause(showClause);
}

export function getBehaviorShowClauses(showClauses: ShowClauseType[]): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses.filter(isBehaviorShowClause);
}

export function getFunnelsShowClauses(showClauses: ShowClauseType[]): BehaviorShowClauseType[] {
  return getBehaviorShowClauses(showClauses).filter(
    (showClause) => showClause?.behavior?.type === MetricType.Funnel && !showClause.isHidden,
  );
}

const findMatchingMetric = (metadata: MetricMetadata, path: string[]) => metadata.metric_key === path[0];

export const findShowClauseIndex = (meta: MetricMetadata[], path: string[]) => {
  return meta.findIndex(
    (item) => findMatchingMetric(item, path) || item[`sub_queries`]?.some((metric) => findMatchingMetric(metric, path)),
  );
};

/**
 * Get BehaviorShowClauses plus any that are in FormulaShowClause.formula.referencedMetrics
 * Note: this function is similar to the method getExpandedBehaviorClauses on ShowSection
 * in ../section.ts (which operates on class types)
 */
export function getExpandedBehaviorShowClauses({
  showClauses,
  metricsResource,
  behaviorsResource,
}: {
  showClauses: ShowClauseType[];
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses
    .map((showClause) => {
      if (isBehaviorShowClause(showClause)) {
        return showClause;
      }
      const resolved = getResolvedFormulaShowClause({
        formulaShowClause: showClause,
        metricsResource,
        behaviorsResource,
      });
      // If formula is rolling, apply to all referenced metrics
      (resolved?.referencedMetrics || []).forEach((x) => {
        if (resolved?.measurement?.rolling && !x?.measurement?.rolling) {
          x.measurement = x.measurement || {};
          x.measurement.rolling = resolved.measurement?.rolling;
        }
      });
      return resolved?.referencedMetrics || [];
    })
    .flat();
}

export function hasGroupByCohort(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.cohorts);
}

export function hasGroupByDuration(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.value === PAGE_DURATION.value);
}

export function hasGroupByUser(group: GroupClauseType[]): boolean {
  return group.some((groupClause) => groupClause.resourceType === InsightsResourceType.People);
}

export function hasRolling(showClauses: ShowClauseType[]): boolean {
  return showClauses.some((clause) => clause.measurement?.rolling);
}

export function hasCumulative(showClauses: ShowClauseType[]): boolean {
  return showClauses.some((clause) => clause.measurement?.cumulative);
}

export function hasCumulativeUnique(showClauses: ShowClauseType[]): boolean {
  return showClauses.some(
    (clause) =>
      clause.measurement &&
      `math` in clause.measurement &&
      clause.measurement.math === PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE,
  );
}

export function getNumerOfEventComparisons(showClause: ShowClauseType): number {
  if (!showClause) return 0;

  const behaviors = isBehaviorShowClause(showClause) ? showClause.behavior?.behaviors : [];
  return (behaviors || []).reduce((acc, beh) => {
    if (`behaviors` in beh) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

export function hasAnyOrderFunnel(showClauses: ShowClauseType[]) {
  return (
    showClauses.some((clause) => isBehaviorShowClause(clause) && clause?.behavior?.type === MetricType.Funnel) &&
    showClauses.some(
      (clause) =>
        isBehaviorShowClause(clause) &&
        clause?.behavior?.behaviors?.some((behavior) => behavior.funnelOrder === FunnelOrder.Any),
    )
  );
}

export function hasEventComparison(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) =>
      isBehaviorShowClause(clause) &&
      clause?.behavior?.type === MetricType.Funnel &&
      clause?.behavior?.behaviors?.some((behavior) => Array.isArray(behavior.behaviors)),
  );
}

export function hasEventBreakdown(groupClauses: GroupClauseType[]) {
  return groupClauses.some((clause) => clause.resourceType === InsightsResourceType.Events);
}

export function hasRetentionFrequency(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) => isBehaviorShowClause(clause) && clause?.behavior?.type === MetricType.RetentionFrequency,
  );
}

export function hasTTC(showClauses: ShowClauseType[]) {
  return showClauses.some(
    (clause) =>
      isBehaviorShowClause(clause) &&
      clause?.measurement?.property &&
      clause?.measurement?.property.name === `$time_to_conversion`,
  );
}

export function getFormulaShowClauses(showClauses: ShowClauseType[]): FormulaShowClauseType[] {
  return showClauses.filter(isFormulaShowClause);
}

export function hasSavedFormula(params: InsightsBookmarkParams) {
  return params.sections.show.some((clause) => {
    return Boolean(clause.id);
  });
}

export function hasSavedBehavior(params: InsightsBookmarkParams) {
  return params.sections.show.some((clause) => {
    if (!isBehaviorShowClause(clause)) {
      return false;
    }

    return Boolean(clause.behavior?.id);
  });
}
/** @todo(metrics) marked for deletion */
export function hydrateSavedBehaviors(show: ShowClauseType, behaviors: BehaviorsResource) {
  if (
    isBehaviorShowClause(show) &&
    show.behavior?.id &&
    `hasUnsavedChanges` in show === false &&
    `hasUnsavedChanges` in show.behavior === false &&
    show.behavior?.type &&
    [MetricType.Retention, MetricType.Funnel].includes(show.behavior.type as MetricType)
  ) {
    // If the existing behavior definition is present, respect it
    if (!isNil(show.behavior?.behaviors)) {
      return show;
    }
    const savedBehavior = behaviors.getOne(show.behavior?.id).value;
    if (!savedBehavior) {
      return show;
    }
    return {
      ...show,
      behavior: {
        ...savedBehavior.definition.behavior,
        id: savedBehavior.id,
        name: savedBehavior.name,
        type: savedBehavior.type,
      } as MetricBehavior,
    };
  }
  return show;
}

enum CustomPropertyReferenceLocation {
  MetricAggregation = `metric_aggregation`,
  MetricFilter = `metric_filter`,
  MetricFilterBehavior = `metric_filter_behavior`,
  Breakdown = `breakdown`,
  BreakdownBehavior = `breakdown_behavior`,
  Filter = `filter`,
  FilterBehavior = `filter_behavior`,
}

type CustomPropertyReference = {
  referenceLocation: CustomPropertyReferenceLocation;
  customPropertyId: number | string;
};

/**
 * NOTE: This function doesn't dedupe custom properties, since it's used to determine total number of borrowed
 * properties in the report and the location of each usage.
 */
export function getCustomPropertyReferences(params: InsightsBookmarkParams): CustomPropertyReference[] {
  const {show, group, filter} = params.sections;
  const customPropertyReferences: CustomPropertyReference[] = [];

  const addReferencesFromBehavior = ({
    behavior,
    referenceLocation,
  }: {
    behavior: Behavior;
    referenceLocation: CustomPropertyReferenceLocation;
  }): void => {
    const behaviorProperty = behavior?.property;
    if (behaviorProperty && `customPropertyId` in behaviorProperty && behaviorProperty.customPropertyId) {
      customPropertyReferences.push({
        referenceLocation,
        customPropertyId: behaviorProperty.customPropertyId,
      });
    }
  };

  const showBehaviorClauses = show.filter(isBehaviorShowClause);
  showBehaviorClauses.forEach((behaviorClause) => {
    behaviorClause.behavior?.filters?.forEach((clause) => {
      if (clause.customPropertyId) {
        customPropertyReferences.push({
          referenceLocation: CustomPropertyReferenceLocation.MetricFilter,
          customPropertyId: clause.customPropertyId,
        });
      }

      addReferencesFromBehavior({
        behavior: clause.behavior,
        referenceLocation: CustomPropertyReferenceLocation.MetricFilterBehavior,
      });
    });

    const measurementCustomPropertyId = behaviorClause.measurement?.property?.customPropertyId;
    if (measurementCustomPropertyId) {
      customPropertyReferences.push({
        referenceLocation: CustomPropertyReferenceLocation.MetricAggregation,
        customPropertyId: measurementCustomPropertyId,
      });
    }
  });

  group?.forEach((clause) => {
    if (clause.customPropertyId) {
      customPropertyReferences.push({
        referenceLocation: CustomPropertyReferenceLocation.Breakdown,
        customPropertyId: clause.customPropertyId,
      });
    }
    addReferencesFromBehavior({
      behavior: clause.behavior,
      referenceLocation: CustomPropertyReferenceLocation.BreakdownBehavior,
    });
  });

  filter?.forEach((clause) => {
    if (clause.customPropertyId) {
      customPropertyReferences.push({
        referenceLocation: CustomPropertyReferenceLocation.Filter,
        customPropertyId: clause.customPropertyId,
      });
    }
    addReferencesFromBehavior({
      behavior: clause.behavior,
      referenceLocation: CustomPropertyReferenceLocation.FilterBehavior,
    });
  });

  return customPropertyReferences;
}

export function getFormulaFromFormulaShowClause(clause: FormulaShowClause): ShowClauseFormula {
  return {
    id: clause.id,
    definition: clause.definition,
    name: clause.name,
    measurement: clause.measurement,
    referencedMetrics: clause.referencedMetrics,
  };
}

export function getResourceTypes(showClauses: ShowClauseType[]): Set<BookmarkTypes.InsightsResourceType> {
  const types = new Set<BookmarkTypes.InsightsResourceType>();

  showClauses.forEach((clause) => {
    if (isBehaviorShowClause(clause)) {
      clause.behavior?.resourceType && types.add(clause.behavior.resourceType);
    } else if (isFormulaShowClause(clause)) {
      clause.referencedMetrics?.forEach(
        (reference) => reference.behavior?.resourceType && types.add(reference.behavior.resourceType),
      );
    }
  });

  return types;
}

export function getMetricTypes(showClauses: ShowClauseType[]): Set<MetricType> {
  const metricTypes = new Set<MetricType>();
  showClauses.forEach((showClause) => {
    if (isBehaviorShowClause(showClause)) {
      showClause.behavior?.type && metricTypes.add(showClause.behavior.type);
    } else if (isFormulaShowClause(showClause)) {
      metricTypes.add(MetricType.Formula);
    }
  });
  return metricTypes;
}

export function isFunnelShowClause(show: ShowClauseType): show is BehaviorShowClauseType {
  return isBehaviorShowClause(show) && show.behavior?.type === MetricType.Funnel;
}

export function isFunnelConversionShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    show.behavior?.type === MetricType.Funnel &&
    [
      FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
      FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
      FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
    ].includes(show.measurement?.math as FUNNELS_MATH_TYPES)
  );
}

export function isFunnelTimeToConvertShowClause(show: ShowClauseType): boolean {
  return (
    isBehaviorShowClause(show) &&
    ((show.behavior?.type === MetricType.Funnel &&
      show.measurement?.property?.name === FUNNELS_TIME_TO_CONVERSION.value) ||
      ((show.behavior?.type === MetricType.Event || show.behavior?.type === MetricType.CustomEvent) &&
        show.measurement?.property?.name === PAGE_DURATION.value))
  );
}

export function isRetentionShowClause(show: ShowClauseType): show is BehaviorShowClauseType {
  return Boolean(
    isBehaviorShowClause(show) &&
      show.behavior?.type &&
      [MetricType.Retention, MetricType.RetentionFrequency].includes(show.behavior.type as MetricType),
  );
}

export function isRetentionRateShowClause(show: ShowClauseType): boolean {
  return Boolean(
    isBehaviorShowClause(show) &&
      show.behavior?.type &&
      [MetricType.Retention, MetricType.RetentionFrequency].includes(show.behavior.type) &&
      show.measurement?.math === RETENTION_MATH_TYPES.RETENTION_RATE,
  );
}

export function isXauShowClause(show: ShowClauseType): boolean {
  return isBehaviorShowClause(show) && XAU_ONLY_MATH_TYPES.includes(show.measurement?.math as XAU_MATH_TYPES);
}

export function isCohortsShowClause(show: ShowClauseType): show is BehaviorShowClauseType {
  return Boolean(
    isBehaviorShowClause(show) &&
      show.behavior?.resourceType &&
      [InsightsResourceType.Cohorts, InsightsResourceType.People].includes(show.behavior.resourceType),
  );
}

export function isAllEventsShowClause(show: ShowClauseType): show is BehaviorShowClauseType {
  return (
    isBehaviorShowClause(show) &&
    Boolean(show.behavior?.behaviors?.some((behavior) => behavior.name === SPECIAL_EVENTS.ALL_EVENTS))
  );
}

export function isFunnelTimeToConvertMetric(bookmark: {sections: BookmarkTypes.Sections}): boolean {
  return showClausesAreFunnelTimeToConvertMetric(bookmark.sections.show);
}

export function showClausesAreFunnelTimeToConvertMetric(showClauses: ShowClauseType[]): boolean {
  return showClauses.length > 0 && showClauses.every(isFunnelTimeToConvertShowClause);
}

export function showClausesAreConversionOrRetentionRateMetric(showClauses: ShowClauseType[]): boolean {
  return (
    showClauses.length > 0 &&
    showClauses.every((show) => isFunnelConversionShowClause(show) || isRetentionRateShowClause(show))
  );
}

export function isConversionOrRetentionRateMetric(bookmark: {sections: BookmarkTypes.Sections}): boolean {
  return showClausesAreConversionOrRetentionRateMetric(bookmark.sections.show);
}

export function isLiftComparisonBookmark(bookmark: InsightsChartParams): boolean {
  return Boolean(bookmark?.liftComparison);
}

export function isConversionOrRetentionRateMetricForAxis(
  bookmark: InsightsBookmarkParams,
  axis: BookmarkTypes.AxisAssignment,
) {
  const clauses = bookmark.sections.show
    // We only care about clauses that are not hidden.
    .filter((c) => !c.isHidden)
    // Make sure to only include clauses that are on the given axis
    .filter((clause) => displayOptionsMatchAxis(clause.display!, axis));

  return showClausesAreConversionOrRetentionRateMetric(clauses);
}

export function isConversionOrRetentionRateEntry(entry: EntryWithDisplay): boolean {
  if (!(`behavior` in entry) || !entry.behavior) {
    return false;
  }
  return (
    (entry.behavior.type === MetricType.Retention &&
      entry.measurement.aggregationOperator === RETENTION_MATH_TYPES.RETENTION_RATE) ||
    (entry.behavior.type === MetricType.Funnel &&
      [
        FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
      ].includes(entry.measurement.aggregationOperator as FUNNELS_MATH_TYPES))
  );
}

export function hasOneFunnelMetric(bookmark: InsightsBookmarkParams): boolean {
  const filteredShowClauses = bookmark.sections.show.filter((show) => !show.isHidden);
  return filteredShowClauses.length === 1 && isFunnelShowClause(filteredShowClauses[0]);
}

export function hasOnlyEventBasedMetrics(bookmark: InsightsBookmarkParams): boolean {
  const types = getResourceTypes(bookmark.sections.show);
  return types.size === 1 && types.has(BookmarkTypes.InsightsResourceType.Events);
}

export function hasEventMetrics(bookmark: InsightsBookmarkParams): boolean {
  const types = getMetricTypes(bookmark.sections.show);
  return types.has(MetricType.Event) || types.has(MetricType.Simple);
}

export function hasPeopleMetrics(bookmark: InsightsBookmarkParams): boolean {
  const types = getMetricTypes(bookmark.sections.show);
  return types.has(MetricType.People);
}

export function hasCohortMetric(bookmark: InsightsBookmarkParams): boolean {
  const types = getMetricTypes(bookmark.sections.show);
  return types.has(MetricType.Cohort);
}

export function hasFunnelMetric(bookmark: InsightsBookmarkParams): boolean {
  const types = getMetricTypes(bookmark.sections.show);
  return types.has(MetricType.Funnel);
}

export function hasOnlyFunnelMetrics(bookmark: InsightsBookmarkParams): boolean {
  const types = getMetricTypes(bookmark.sections.show);
  return types.size === 1 && types.has(MetricType.Funnel);
}

export function hasFunnelConversionMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isFunnelConversionShowClause);
}

export function hasFunnelTimeToConvertMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isFunnelTimeToConvertShowClause);
}

export function hasRetentionMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isRetentionShowClause);
}

export function hasRetentionRateMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isRetentionRateShowClause);
}

export function hasXauMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isXauShowClause);
}

export function hasCohortsOverTimeMetric(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.show.some(isCohortsShowClause);
}

export function isFunnelOnlyInsightsBookmark(
  bookmark: InsightsBookmarkParams | FunnelBookmark | FlowsBookmark | RetentionBookmarkParams,
): boolean {
  if (!isInsightsBookmarkParams(bookmark) || bookmark.sections.show.length === 0) {
    return false;
  }
  // TODO(CC): Handle formula - If the formula's referenced metrics are funnel metrics only, return true
  const filteredShowClauses = bookmark.sections.show.filter((show) => !show.isHidden);
  return filteredShowClauses.every(isFunnelShowClause);
}

export function isRetentionOnlyInsightsBookmark(
  bookmark: InsightsBookmarkParams | FunnelBookmark | FlowsBookmark | RetentionBookmarkParams,
): boolean {
  if (!isInsightsBookmarkParams(bookmark) || bookmark.sections.show.length === 0) {
    return false;
  }
  // TODO(CC): Handle formula - If the referenced metrics are retention metrics only, return true
  const filteredShowClauses = bookmark.sections.show.filter((show) => !show.isHidden);
  return filteredShowClauses.every(isRetentionShowClause);
}

export function hasFunnelTimeToConvertBreakdownBookmark(bookmark: InsightsBookmarkParams): boolean {
  return bookmark.sections.group?.some((group) => group.value === FUNNELS_TIME_TO_CONVERSION.value) ?? false;
}

export function getMaxStepIndex(showClauses: ShowClauseType[]): number {
  return (
    Math.max(
      ...showClauses.map((showClause) => {
        if (isFormulaShowClause(showClause)) {
          return 0;
        }
        if (isBehaviorShowClause(showClause) && Array.isArray(showClause.behavior?.behaviors)) {
          return showClause.behavior?.behaviors.length;
        }
        return 1;
      }),
    ) - 1
  );
}

export function getDashboardDataTableColumnWidth(bookmarkParams: InsightsBookmarkParams) {
  return (
    bookmarkParams.columnWidths?.dataTable?.dashboard ?? bookmarkParams.columnWidths?.dataTable?.report ?? undefined
  );
}

export function isCohortFilter(filter: BookmarkTypes.JsonValue | null) {
  if (!filter) {
    return false;
  }
  return (
    filter.value === INSIGHTS_COHORT_TYPE &&
    Array.isArray(filter.filterValue) &&
    filter.filterValue &&
    filter.filterValue.length > 0 &&
    `cohort` in filter.filterValue[0]
  );
}

export function isEventFilter(filter: BookmarkTypes.JsonValue | null) {
  if (!filter) {
    return false;
  }
  return filter.resourceType === BookmarkTypes.InsightsResourceType.Events && !isCohortFilter(filter);
}
